import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import type { CashflowMatrix, CashflowRow, CashflowCell, CellSource } from '../../hooks/useCashflow';

export type CashflowCellContext = {
  rowKey: string;
  rowLabel: string;
  ym: number;
  monthLabel: string;
  source: CellSource;
  value: number;
};

type Props = {
  matrix: CashflowMatrix;
  onCellClick?: (ctx: CashflowCellContext) => void;
};

// セル出所ごとの表示スタイル (実績は黒、予定は出所で色分け)
export const SOURCE_STYLE: Record<CellSource, { color: string; label: string }> = {
  actual: { color: '#111827', label: '実績 (OBC 仕訳)' },
  payable: { color: '#1d4ed8', label: '計上実データ (Hozenall NET・工事管理)' },
  budget: { color: '#047857', label: '予算 (OBC 販管費)' },
  obc_plan: { color: '#6b7280', label: 'OBC 予定伝票' },
  average: { color: '#b45309', label: '直近 3 か月平均' },
};

// 千円表示 (0 は '-')。マイナスは △ 付きで区別する
export const fmtK = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '-';
  const k = Math.round(v / 1000);
  if (k === 0) return '-';
  return k < 0 ? `△${Math.abs(k).toLocaleString()}` : k.toLocaleString();
};

const sumSection = (rows: CashflowRow[], section: 'in' | 'out', ym: number): number =>
  rows.filter(r => r.section === section).reduce((s, r) => s + (r.cells[ym]?.value || 0), 0);

/**
 * 資金繰り表テーブル。
 * 列 = 月 (実績 / 予定)、行 = 期首残高 → 収入 → 支出 → 財務 → 純増減 → 期末残高。
 * 買掛金支払行は元システム別の内訳を開閉できる。セルをクリックすると明細ダイアログを開く。
 */
export default function CashflowTable({ matrix, onCellClick }: Props) {
  const { months, rows, balance } = matrix;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ OUT_AP: true });

  const headSx = (kind: 'actual' | 'forecast') => ({
    backgroundColor: kind === 'actual' ? '#f5f5f5' : '#eef2ff',
    color: kind === 'actual' ? 'grey.600' : '#3730a3',
    fontWeight: 600,
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
    minWidth: '104px',
    zIndex: 1,
    textAlign: 'right' as const,
  });
  const labelCellSx = { position: 'sticky' as const, left: 0, zIndex: 2, backgroundColor: '#fff', whiteSpace: 'nowrap', fontSize: '0.8125rem' };
  const sectionSx = { backgroundColor: '#f8fafc', fontWeight: 700, fontSize: '0.8125rem', color: '#334155' };
  const numSx = { fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', whiteSpace: 'nowrap' };

  const renderCell = (row: CashflowRow, ym: number, monthLabel: string, cell: CashflowCell | undefined, indent = 0) => {
    const source = cell?.source ?? 'actual';
    const value = cell?.value ?? 0;
    const style = SOURCE_STYLE[source];
    const clickable = !!onCellClick && value !== 0;
    const tip = source === 'payable' && cell?.obc_plan !== undefined
      ? `${style.label} / OBC 予定伝票 ${fmtK(cell.obc_plan)} 千円`
      : source === 'budget' && cell?.version
        ? `${style.label} ${cell.version} / OBC 予定伝票 ${fmtK(cell.obc_plan)} 千円`
        : style.label;
    return (
      <Tooltip key={`${row.key}-${ym}`} title={tip} placement="top" enterDelay={400}>
        <TableCell
          align="right"
          onClick={clickable ? () => onCellClick!({ rowKey: row.key, rowLabel: row.label, ym, monthLabel, source, value }) : undefined}
          sx={{
            ...numSx,
            color: style.color,
            fontStyle: source === 'obc_plan' ? 'italic' : 'normal',
            cursor: clickable ? 'pointer' : 'default',
            pl: indent ? 2 : undefined,
            '&:hover': clickable ? { backgroundColor: '#e0e7ff', textDecoration: 'underline' } : undefined,
          }}
        >
          {fmtK(value)}
        </TableCell>
      </Tooltip>
    );
  };

  const renderRow = (row: CashflowRow, indent = 0) => (
    <React.Fragment key={row.key}>
      <TableRow hover>
        <TableCell sx={{ ...labelCellSx, pl: 2 + indent * 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {(row.children?.length ?? 0) > 0 ? (
              <IconButton size="small" onClick={() => setExpanded(p => ({ ...p, [row.key]: !p[row.key] }))} sx={{ p: 0.25 }}>
                {expanded[row.key] ? <Icons.ExpandMore fontSize="small" /> : <Icons.ChevronRight fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 24 }} />
            )}
            <span style={{ color: indent ? '#64748b' : undefined, fontSize: indent ? '0.75rem' : undefined }}>{row.label}</span>
          </Box>
        </TableCell>
        {months.map(m => renderCell(row, m.ym, m.label, row.cells[m.ym], indent))}
      </TableRow>
      {expanded[row.key] && (row.children ?? []).map(ch => renderRow(ch, indent + 1))}
    </React.Fragment>
  );

  const renderTotalRow = (label: string, values: Record<number, number | null | undefined>, opts?: { strong?: boolean; bg?: string; color?: string; italic?: boolean }) => (
    <TableRow>
      <TableCell sx={{ ...labelCellSx, ...(opts?.bg ? { backgroundColor: opts.bg } : {}), fontWeight: opts?.strong ? 700 : 500, color: opts?.color, fontStyle: opts?.italic ? 'italic' : 'normal' }}>{label}</TableCell>
      {months.map(m => (
        <TableCell key={m.ym} align="right" sx={{ ...numSx, backgroundColor: opts?.bg, fontWeight: opts?.strong ? 700 : 500, color: opts?.color, fontStyle: opts?.italic ? 'italic' : 'normal' }}>
          {fmtK(values[m.ym])}
        </TableCell>
      ))}
    </TableRow>
  );

  const sectionHeader = (label: string) => (
    <TableRow>
      <TableCell sx={{ ...labelCellSx, ...sectionSx }}>{label}</TableCell>
      {months.map(m => <TableCell key={m.ym} sx={sectionSx} />)}
    </TableRow>
  );

  const inTotals: Record<number, number> = {};
  const outTotals: Record<number, number> = {};
  months.forEach(m => { inTotals[m.ym] = sumSection(rows, 'in', m.ym); outTotals[m.ym] = sumSection(rows, 'out', m.ym); });

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgb(0 0 0 / 0.12)', overflow: 'auto', flex: 1, minHeight: 0 }}>
        <Table stickyHeader size="small" sx={{ width: '100%', minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...labelCellSx, backgroundColor: '#f5f5f5', color: 'grey.600', fontWeight: 600, minWidth: '260px', zIndex: 11 }}>
                区分 (単位 千円)
              </TableCell>
              {months.map(m => (
                <TableCell key={m.ym} sx={headSx(m.kind)}>
                  <div>{m.label}</div>
                  <div style={{ fontSize: '0.65rem', fontWeight: 500 }}>{m.kind === 'actual' ? '実績' : '予定'}</div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTotalRow('期首 現金及び預金', balance.opening, { strong: true, bg: '#fafafa' })}

            {sectionHeader('収入')}
            {rows.filter(r => r.section === 'in').map(r => renderRow(r))}
            {renderTotalRow('収入 計', inTotals, { strong: true })}

            {sectionHeader('支出')}
            {rows.filter(r => r.section === 'out').map(r => renderRow(r))}
            {renderTotalRow('支出 計', outTotals, { strong: true })}

            {sectionHeader('財務 (借入)')}
            {rows.filter(r => r.section === 'fin').map(r => renderRow(r))}

            {renderTotalRow('純増減 (収入 − 支出 + 借入 − 返済)', balance.net, { strong: true, bg: '#fafafa' })}
            {renderTotalRow('期末 現金及び預金', balance.closing, { strong: true, bg: '#eef2ff', color: '#1e3a8a' })}
            {renderTotalRow('参考 OBC 貸借対照表の現預金 (予定伝票込み)', balance.bs_closing, { italic: true, color: '#6b7280' })}
            {renderTotalRow('検算 B/S − 計算 (実績月)', balance.diff, { italic: true, color: '#6b7280' })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
